import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { Settings, Bell, Lock, Info, Moon, Sun, Monitor, Loader } from 'lucide-react';

export const SettingsPage = () => {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('general');

  if (!user) {
    navigate('/login');
    return null;
  }

  const sections = [
    { id: 'general', label: 'Général', icon: Settings },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Confidentialité', icon: Lock },
    { id: 'about', label: 'À propos', icon: Info },
  ];

  return (
    <div className="min-h-screen bg-background pt-20 pb-12">
      <div className="container max-w-5xl mx-auto px-4">
        <h1 className="text-3xl font-bold text-foreground mb-8">Paramètres</h1>

        <div className="flex flex-col md:flex-row gap-6">
          {/* Sidebar */}
          <div className="w-full md:w-64 flex-shrink-0">
            <div className="bg-card border border-border rounded-lg p-2 space-y-1">
              {sections.map((section) => {
                const Icon = section.icon;
                return (
                  <button
                    key={section.id}
                    onClick={() => setActiveSection(section.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent text-foreground'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="font-medium">{section.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className="bg-card border border-border rounded-lg p-6">
              {activeSection === 'general' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-foreground mb-4">Général</h2>
                  </div>

                  {/* Theme */}
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-3">
                      Thème d'affichage
                    </label>
                    <div className="grid grid-cols-3 gap-3">
                      <button
                        onClick={() => setTheme('light')}
                        className={`p-4 rounded-lg border-2 transition-colors ${
                          theme === 'light'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <Sun className="w-6 h-6 mx-auto mb-2 text-foreground" />
                        <p className="text-sm font-medium text-foreground">Clair</p>
                      </button>
                      <button
                        onClick={() => setTheme('dark')}
                        className={`p-4 rounded-lg border-2 transition-colors ${
                          theme === 'dark'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <Moon className="w-6 h-6 mx-auto mb-2 text-foreground" />
                        <p className="text-sm font-medium text-foreground">Sombre</p>
                      </button>
                      <button
                        onClick={() => setTheme('system')}
                        className={`p-4 rounded-lg border-2 transition-colors ${
                          theme === 'system'
                            ? 'border-primary bg-primary/10'
                            : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <Monitor className="w-6 h-6 mx-auto mb-2 text-foreground" />
                        <p className="text-sm font-medium text-foreground">Système</p>
                      </button>
                    </div>
                  </div>

                  {/* Account Info */}
                  <div className="pt-6 border-t border-border">
                    <h3 className="font-medium text-foreground mb-4">Informations du compte</h3>
                    <div className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Email</p>
                        <p className="text-foreground">{user.email}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Nom d'utilisateur</p>
                        <p className="text-foreground">{user.username}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Rôle</p>
                        <p className="text-foreground capitalize">{user.role}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'notifications' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-foreground mb-2">Notifications</h2>
                    <p className="text-sm text-muted-foreground">
                      Gérez vos préférences de notification
                    </p>
                  </div>

                  <div className="space-y-4">
                    <label className="flex items-center justify-between p-4 bg-accent rounded-lg cursor-pointer">
                      <div>
                        <p className="font-medium text-foreground">Notifications push</p>
                        <p className="text-sm text-muted-foreground">Recevoir des notifications sur ce navigateur</p>
                      </div>
                      <input type="checkbox" defaultChecked className="w-5 h-5" />
                    </label>

                    <label className="flex items-center justify-between p-4 bg-accent rounded-lg cursor-pointer">
                      <div>
                        <p className="font-medium text-foreground">Nouveaux contenus</p>
                        <p className="text-sm text-muted-foreground">Nouveaux uploads de créateurs suivis</p>
                      </div>
                      <input type="checkbox" defaultChecked className="w-5 h-5" />
                    </label>

                    <label className="flex items-center justify-between p-4 bg-accent rounded-lg cursor-pointer">
                      <div>
                        <p className="font-medium text-foreground">Actualités</p>
                        <p className="text-sm text-muted-foreground">Nouveautés et mises à jour de YetuTv</p>
                      </div>
                      <input type="checkbox" defaultChecked className="w-5 h-5" />
                    </label>
                  </div>
                </div>
              )}

              {activeSection === 'privacy' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-foreground mb-2">Confidentialité</h2>
                    <p className="text-sm text-muted-foreground">
                      Contrôlez vos données et votre confidentialité
                    </p>
                  </div>

                  <div className="space-y-4">
                    <label className="flex items-center justify-between p-4 bg-accent rounded-lg cursor-pointer">
                      <div>
                        <p className="font-medium text-foreground">Historique de visionnage</p>
                        <p className="text-sm text-muted-foreground">Sauvegarder mon historique de visionnage</p>
                      </div>
                      <input type="checkbox" defaultChecked className="w-5 h-5" />
                    </label>

                    <label className="flex items-center justify-between p-4 bg-accent rounded-lg cursor-pointer">
                      <div>
                        <p className="font-medium text-foreground">Profil public</p>
                        <p className="text-sm text-muted-foreground">Autoriser les autres à voir mon profil</p>
                      </div>
                      <input type="checkbox" className="w-5 h-5" />
                    </label>

                    <div className="pt-4">
                      <button className="text-destructive hover:underline text-sm">
                        Effacer mon historique de visionnage
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'about' && (
                <div className="space-y-6">
                  <div>
                    <h2 className="text-xl font-bold text-foreground mb-2">À propos de YetuTv</h2>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 bg-accent rounded-lg">
                      <p className="text-sm text-muted-foreground mb-1">Version</p>
                      <p className="font-medium text-foreground">1.0.0 Beta</p>
                    </div>

                    <div className="space-y-2">
                      <a
                        href="#"
                        className="block p-4 bg-accent rounded-lg hover:bg-accent/80 transition-colors"
                      >
                        <p className="font-medium text-foreground">Conditions d'utilisation</p>
                      </a>
                      <a
                        href="#"
                        className="block p-4 bg-accent rounded-lg hover:bg-accent/80 transition-colors"
                      >
                        <p className="font-medium text-foreground">Politique de confidentialité</p>
                      </a>
                      <a
                        href="#"
                        className="block p-4 bg-accent rounded-lg hover:bg-accent/80 transition-colors"
                      >
                        <p className="font-medium text-foreground">Politique du droit d'auteur</p>
                      </a>
                    </div>

                    <div className="pt-4 text-center">
                      <p className="text-sm text-muted-foreground">
                        © 2026 YetuTv. Tous droits réservés.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Logout */}
            <div className="mt-6">
              <button
                onClick={logout}
                className="w-full bg-destructive text-destructive-foreground py-3 rounded-lg font-medium hover:bg-destructive/90 transition-colors"
              >
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
